import React from 'react';
import Styled from './styled';
import {
    AgentIndicator,
    BotIndicator,
    ReasoningIndicator,
} from '../index';
import { MessageParticipantRole } from '@amzn/ring-cs-chatbot-service-model';
import { translated } from '../../utils/TranslationUtil';

export type EntryProps = {
    /** Role of the participant, defaults to 'CUSTOM_BOT'. */
    participantRole?: MessageParticipantRole;
    /** Timestamp associated with the Chat Entry (ISO 8601 Format) */
    timestamp?: number;
    /** Children content to display inside the chat bubble. */
    children?: React.ReactNode | string;
    /** Support loading state for chat message */
    loading?: boolean;
    /** Support reasoning text for chat message */
    reasoningText?: string;
};

/**
 * Entry component that functions as a wrapper for Chat Bubble's within (children).
 * Supports indicator and timestamp associated with each 'entry'
 * 
 * @param {EntryProps} props - The properties to configure the Entry component.
 * @returns {JSX.Element} The rendered Entry component.
 */
export const Entry: React.FC<EntryProps> = ({
    participantRole = MessageParticipantRole.CUSTOM_BOT,
    timestamp,
    children,
    loading = false,
    reasoningText = '',
}) => {
    const formatTimestamp = (timestamp: string | number): string => {
        const date = new Date(timestamp);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const formattedHours = hours < 10 ? `0${hours}` : hours;
        return `${formattedHours}:${formattedMinutes}`;
    };

    const formattedTimestamp = timestamp && formatTimestamp(timestamp);

    // Determine Chat bubble orientation
    const customer = participantRole === MessageParticipantRole.CUSTOMER;
    const isAgent = participantRole === MessageParticipantRole.AGENT;

    // Determine if the indicator/timestamp should be shown
    const showIndicator = !loading && participantRole && !customer;

    return (
        <Styled.Entry customer={customer}>
            {showIndicator && (
                <Styled.IndicatorWrapper>
                    <Styled.IndicatorIcon>
                        {isAgent ? <AgentIndicator /> : <BotIndicator />}
                    </Styled.IndicatorIcon>
                    {!isAgent && reasoningText && (
                        <ReasoningIndicator
                            ariaLabel={translated('Components.Entry.reasoningAriaLabel')}
                            text={reasoningText}
                        />
                    )}
                </Styled.IndicatorWrapper>
            )}
            {children}
            {!!formattedTimestamp &&
                <Styled.Timestamp customer={customer}>
                    {formattedTimestamp}
                </Styled.Timestamp>
            }
        </Styled.Entry>
    );
};
